import { allPass, ifElse, pipe } from 'ramda'
import { BehaviorSubject, map, Observable, of, switchMap, take, tap } from 'rxjs'

import { CyberSourceFlexKeyDocument, CyberSourceFlexKeyMutation } from '../../graphql/magento'
import { GraphQLResult, graphQLResultFrom } from '../graphql'
import { latestApolloClient$ } from '../graphql/apollo'

type WithTime<T> = [T, number]

type GraphQLResultWithTime<
  TData = { [key: string]: unknown },
  TExtensions = { [key: string]: unknown },
> = WithTime<GraphQLResult<TData, TExtensions>>

const snapshotFrom = <T>([snapshot]: WithTime<T>): T => snapshot

const timeFrom = <T>([, timestamp]: WithTime<T>): number => timestamp

const withTime =
  (timestamp: () => number) =>
  <T>(x: T): WithTime<T> => [x, timestamp()]

const cachedFlexKey$ =
  new BehaviorSubject<GraphQLResultWithTime<CyberSourceFlexKeyMutation> | null>(null)

const graphQLResultWithTimeFrom = pipe(
  graphQLResultFrom,
  withTime(() => Date.now()),
)

const generateFlexKey$ = latestApolloClient$.pipe(
  switchMap(
    (client): Promise<GraphQLResultWithTime<CyberSourceFlexKeyMutation>> =>
      client
        .mutate({ fetchPolicy: 'network-only', mutation: CyberSourceFlexKeyDocument })
        .then(graphQLResultWithTimeFrom)
        .catch((err) =>
          withTime(() => Date.now())({
            error: err instanceof Error ? err : new Error(`${err}`),
          }),
        ),
  ),
  tap((result) => {
    // cache result
    cachedFlexKey$.next(result)
  }),
)

const isNotExpired =
  (validDuration: number, currentTime: () => number) =>
  <T>(result: WithTime<T> | null): result is WithTime<T> =>
    !!result && timeFrom(result) + Math.max(0, validDuration) > currentTime()

const isGraphQLResultRight = (
  result: GraphQLResultWithTime<CyberSourceFlexKeyMutation> | null,
): result is GraphQLResultWithTime<CyberSourceFlexKeyMutation> =>
  !!result && !!result[0].data && !result[0].error

const fiveMinutes = 300000

export const currentFlexKey$: Observable<GraphQLResult<CyberSourceFlexKeyMutation>> =
  cachedFlexKey$.pipe(
    // get current value of behavior subject but prevent infinite loop
    take(1),
    switchMap(
      ifElse(
        allPass([isGraphQLResultRight, isNotExpired(fiveMinutes, () => Date.now())]),
        // if cached result is still valid, return as is
        of,
        // if cached result is not found, expired, or had an error, fetch a new result
        () => generateFlexKey$,
      ),
    ),
    map(snapshotFrom),
  )

export const invalidateFlexKey = (): void => {
  cachedFlexKey$.next(null)
}
